// Analytics utility for tracking user interactions
/* global gtag */
class Analytics {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  // Track page views
  trackPageView(pageName) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'page_view', {
        page_title: pageName,
        page_location: window.location.href
      });
    }
  }

  // Track file upload events
  trackFileUpload(fileType, fileSize) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'file_upload', {
        event_category: 'engagement',
        event_label: fileType,
        value: Math.round(fileSize / 1024 / 1024), // File size in MB
        custom_parameter_1: fileType,
        custom_parameter_2: fileSize
      });
    }
  }

  // Track analysis start
  trackAnalysisStart(trackTitle, artistName) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'analysis_started', {
        event_category: 'engagement',
        event_label: 'music_analysis',
        custom_parameter_1: trackTitle || 'Unknown',
        custom_parameter_2: artistName || 'Unknown'
      });
    }
  }

  // Track analysis completion
  trackAnalysisComplete(scores, hasLyrics) {
    if (this.isProduction && window.gtag) {
      const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length;
      
      gtag('event', 'analysis_completed', {
        event_category: 'engagement',
        event_label: 'music_analysis',
        value: Math.round(avgScore),
        custom_parameter_1: avgScore,
        custom_parameter_2: hasLyrics ? 'with_lyrics' : 'without_lyrics',
        custom_parameter_3: JSON.stringify(scores)
      });
    }
  }

  // Track analysis errors
  trackAnalysisError(errorType, errorMessage) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'analysis_error', {
        event_category: 'error',
        event_label: errorType,
        custom_parameter_1: errorMessage
      });
    }
  }

  // Track user engagement with results
  trackResultsInteraction(interactionType, category = null) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'results_interaction', {
        event_category: 'engagement',
        event_label: interactionType,
        custom_parameter_1: category
      });
    }
  }

  // Track similar artists clicks
  trackSimilarArtistClick(artistName) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'similar_artist_click', {
        event_category: 'engagement',
        event_label: 'similar_artists',
        custom_parameter_1: artistName
      });
    }
  }

  // Track session duration
  trackSessionDuration(duration) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'session_duration', {
        event_category: 'engagement',
        value: Math.round(duration / 1000), // Duration in seconds
        custom_parameter_1: duration
      });
    }
  }

  // Track user return visits
  trackReturnVisit() {
    if (this.isProduction && window.gtag) {
      gtag('event', 'return_visit', {
        event_category: 'engagement',
        event_label: 'user_retention'
      });
    }
  }

  // Track form interactions
  trackFormInteraction(fieldName, action) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'form_interaction', {
        event_category: 'engagement',
        event_label: action,
        custom_parameter_1: fieldName
      });
    }
  }

  // Track button clicks
  trackButtonClick(buttonName, location) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'button_click', {
        event_category: 'engagement',
        event_label: buttonName,
        custom_parameter_1: location
      });
    }
  }

  // Track file selection
  trackFileSelection(fileType, fileSize) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'file_selected', {
        event_category: 'engagement',
        event_label: 'file_selection',
        custom_parameter_1: fileType,
        custom_parameter_2: Math.round(fileSize / 1024 / 1024) // Size in MB
      });
    }
  }

  // Track performance metrics
  trackPerformance(metric, value) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'performance', {
        event_category: 'performance',
        event_label: metric,
        value: Math.round(value),
        custom_parameter_1: value
      });
    }
  }

  // Track purchase events for ecommerce
  trackPurchase(transactionId, value, currency, items, couponCode = null) {
    if (this.isProduction && window.gtag) {
      // Track the purchase event
      gtag('event', 'purchase', {
        transaction_id: transactionId,
        value: value,
        currency: currency,
        items: items,
        coupon: couponCode
      });

      // Also track as a custom event for additional context
      gtag('event', 'purchase_completed', {
        event_category: 'ecommerce',
        event_label: 'purchase',
        value: Math.round(value * 100), // Convert to cents for consistency
        custom_parameter_1: transactionId,
        custom_parameter_2: currency,
        custom_parameter_3: couponCode || 'none',
        custom_parameter_4: items.length // Number of items
      });
    }
  }

  // Track subscription events
  trackSubscription(subscriptionId, sessionId, value, currency, planName = 'CriticAI Monthly Subscription') {
    if (this.isProduction && window.gtag) {
      // Track subscription event with ecommerce data
      gtag('event', 'subscription_purchase', {
        event_category: 'ecommerce',
        event_label: 'subscription',
        value: Math.round(value * 100), // Convert to cents
        currency: currency,
        transaction_id: sessionId,
        subscription_id: subscriptionId,
        plan_name: planName,
        custom_parameter_1: subscriptionId,
        custom_parameter_2: sessionId,
        custom_parameter_3: planName,
        custom_parameter_4: value
      });

      // Also track as a purchase event for ecommerce tracking
      gtag('event', 'purchase', {
        transaction_id: sessionId,
        value: value,
        currency: currency,
        items: [{
          item_id: 'subscription',
          item_name: planName,
          category: 'Subscription',
          quantity: 1,
          price: value
        }]
      });
    }
  }

  // Track checkout start by plan type/tier
  trackCheckoutStarted({
    paymentType,
    tierId,
    tierName,
    quantity = 1,
    value = 0,
    currency = 'USD',
    couponCode = null,
  }) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'begin_checkout', {
        currency,
        value,
        coupon: couponCode || undefined,
        items: [
          {
            item_id: tierId,
            item_name: tierName,
            category: paymentType === 'subscription' ? 'Subscription' : 'Pay Per Use',
            quantity,
            price: quantity > 0 ? value / quantity : value,
          },
        ],
      });

      gtag('event', 'checkout_started', {
        event_category: 'ecommerce',
        event_label: paymentType,
        value: Math.round(value * 100),
        custom_parameter_1: tierId,
        custom_parameter_2: tierName,
        custom_parameter_3: quantity,
        custom_parameter_4: couponCode || 'none',
      });
    }
  }

  // Track pricing card / plan picks
  trackPricingTierSelection(paymentType, tierId, tierName, value) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'pricing_tier_selected', {
        event_category: 'engagement',
        event_label: paymentType,
        value: Math.round((value || 0) * 100),
        custom_parameter_1: tierId,
        custom_parameter_2: tierName,
      });
    }
  }

  // Track auth events
  trackAuthEvent(eventName, method = 'google') {
    if (this.isProduction && window.gtag) {
      gtag('event', eventName, {
        event_category: 'auth',
        event_label: method,
        method,
      });
    }
  }

  // Track explicit analysis CTA clicks
  trackAnalyzeClick(craftType) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'analyze_click', {
        event_category: 'engagement',
        event_label: craftType,
        custom_parameter_1: craftType,
      });
    }
  }

  // Track when a result view is rendered
  trackAnalysisResultViewed(craftType) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'analysis_result_viewed', {
        event_category: 'engagement',
        event_label: craftType,
        custom_parameter_1: craftType,
      });
    }
  }

  // Track users retrying failed analyses
  trackAnalysisRetried(craftType) {
    if (this.isProduction && window.gtag) {
      gtag('event', 'analysis_retried', {
        event_category: 'engagement',
        event_label: craftType,
        custom_parameter_1: craftType,
      });
    }
  }

  // Track exhausted run balance to monitor monetization friction
  trackCreditsExhausted(context = 'analysis_submit') {
    if (this.isProduction && window.gtag) {
      gtag('event', 'credits_exhausted', {
        event_category: 'monetization',
        event_label: context,
        custom_parameter_1: context,
      });
    }
  }

  // Track add_payment_info step before Stripe redirect
  trackAddPaymentInfo(paymentType, tierId, value, currency = 'USD') {
    if (this.isProduction && window.gtag) {
      gtag('event', 'add_payment_info', {
        currency,
        value,
        payment_type: paymentType,
        items: [
          {
            item_id: tierId,
            item_name: tierId,
            quantity: 1,
            price: value,
          },
        ],
      });
    }
  }

  // Track subscription cancellations detected in app session sync
  trackSubscriptionCancelled(source = 'session_sync') {
    if (this.isProduction && window.gtag) {
      gtag('event', 'subscription_cancelled', {
        event_category: 'subscription',
        event_label: source,
        custom_parameter_1: source,
      });
    }
  }

  // Track refund events when backend/webhook surfaces one
  trackRefund(transactionId, value, currency = 'USD', reason = 'unknown') {
    if (this.isProduction && window.gtag) {
      gtag('event', 'refund', {
        transaction_id: transactionId,
        value,
        currency,
      });

      gtag('event', 'refund_issued', {
        event_category: 'ecommerce',
        event_label: reason,
        value: Math.round((value || 0) * 100),
        custom_parameter_1: transactionId,
        custom_parameter_2: reason,
      });
    }
  }

  // Track lead capture actions
  trackGenerateLead(leadType = 'unknown', source = 'unknown') {
    if (this.isProduction && window.gtag) {
      gtag('event', 'generate_lead', {
        event_category: 'lead',
        event_label: leadType,
        custom_parameter_1: source,
      });
    }
  }
}

// Create and export analytics instance
const analytics = new Analytics();
export default analytics; 